import { AuditLogResponse } from '../../audit/models/audit.model';

export type FormTimelineVisualType =
  | 'CREATE' | 'UPDATE' | 'DELETE'
  | 'PUBLISH' | 'PAUSE' | 'ARCHIVE' | 'UNARCHIVE'
  | 'FIELD_ADD' | 'FIELD_UPDATE' | 'FIELD_REMOVE'
  | 'SETTING_INIT' | 'SETTING_UPDATE'
  | 'CRM_MAP_ADD' | 'CRM_MAP_UPDATE' | 'CRM_MAP_REMOVE'
  | 'OPTION_ADD' | 'OPTION_UPDATE' | 'OPTION_REMOVE'
  | 'TARGET_URL_ADD' | 'TARGET_URL_REMOVE';

export interface FormTimelineEntry extends AuditLogResponse {
  visualType: FormTimelineVisualType;
}

function resolveVisualType(log: AuditLogResponse): FormTimelineVisualType {
  const op  = log.operationType?.toUpperCase();
  const sub = log.operationSubtype?.toUpperCase();

  if (op === 'DELETE') return 'DELETE';

  if (sub) {
    switch (sub) {
      case 'FIELD_ADD':          return 'FIELD_ADD';
      case 'FIELD_UPDATE':       return 'FIELD_UPDATE';
      case 'FIELD_REMOVE':       return 'FIELD_REMOVE';
      case 'SETTING_INIT':       return 'SETTING_INIT';
      case 'SETTING_UPDATE':     return 'SETTING_UPDATE';
      case 'CRM_MAP_ADD':        return 'CRM_MAP_ADD';
      case 'CRM_MAP_UPDATE':     return 'CRM_MAP_UPDATE';
      case 'CRM_MAP_REMOVE':     return 'CRM_MAP_REMOVE';
      case 'OPTION_ADD':         return 'OPTION_ADD';
      case 'OPTION_UPDATE':      return 'OPTION_UPDATE';
      case 'OPTION_REMOVE':      return 'OPTION_REMOVE';
      case 'TARGET_URL_ADD':     return 'TARGET_URL_ADD';
      case 'TARGET_URL_REMOVE':  return 'TARGET_URL_REMOVE';
    }
  }

  if (op === 'INSERT' || op === 'CREATE') return 'CREATE';

  // For plain UPDATE: check if changesDisplay has a status field to infer intent
  if (op === 'UPDATE') {
    const statusRow = log.changesDisplay?.find(c => c.field === 'status');
    if (statusRow) {
      const rawNew = statusRow.newValue?.toUpperCase();
      const rawOld = statusRow.oldValue?.toUpperCase();
      if (rawNew === 'PUBLISHED') return 'PUBLISH';
      if (rawNew === 'PAUSED')    return 'PAUSE';
      if (rawNew === 'ARCHIVED')  return 'ARCHIVE';
      if (rawOld === 'ARCHIVED')  return 'UNARCHIVE';
    }
  }

  return 'UPDATE';
}

export function enrichFormTimeline(logs: AuditLogResponse[]): FormTimelineEntry[] {
  return (logs ?? []).map(log => ({ ...log, visualType: resolveVisualType(log) }));
}

// ─── Badge ───────────────────────────────────────────────────────────────────

const BADGE_LABELS: Record<FormTimelineVisualType, string> = {
  CREATE:            'CREACIÓN',
  UPDATE:            'ACTUALIZACIÓN',
  DELETE:            'ELIMINACIÓN',
  PUBLISH:           'PUBLICACIÓN',
  PAUSE:             'PAUSADO',
  ARCHIVE:           'ARCHIVADO',
  UNARCHIVE:         'DESARCHIVADO',
  FIELD_ADD:         'CAMPO',
  FIELD_UPDATE:      'CAMPO',
  FIELD_REMOVE:      'CAMPO',
  SETTING_INIT:      'CONFIGURACIÓN',
  SETTING_UPDATE:    'CONFIGURACIÓN',
  CRM_MAP_ADD:       'MAPEO CRM',
  CRM_MAP_UPDATE:    'MAPEO CRM',
  CRM_MAP_REMOVE:    'MAPEO CRM',
  OPTION_ADD:        'OPCIÓN',
  OPTION_UPDATE:     'OPCIÓN',
  OPTION_REMOVE:     'OPCIÓN',
  TARGET_URL_ADD:    'URL',
  TARGET_URL_REMOVE: 'URL',
};

const BADGE_CLASSES: Record<FormTimelineVisualType, string> = {
  CREATE:            'bg-green-100 text-green-700 dark:bg-green-500/10 dark:text-green-400',
  UPDATE:            'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
  DELETE:            'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  PUBLISH:           'bg-teal-100 text-teal-700 dark:bg-teal-500/10 dark:text-teal-400',
  PAUSE:             'bg-amber-100 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400',
  ARCHIVE:           'bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300',
  UNARCHIVE:         'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400',
  FIELD_ADD:         'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
  FIELD_UPDATE:      'bg-blue-100 text-blue-700 dark:bg-blue-500/10 dark:text-blue-400',
  FIELD_REMOVE:      'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  SETTING_INIT:      'bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400',
  SETTING_UPDATE:    'bg-purple-100 text-purple-700 dark:bg-purple-500/10 dark:text-purple-400',
  CRM_MAP_ADD:       'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400',
  CRM_MAP_UPDATE:    'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/10 dark:text-cyan-400',
  CRM_MAP_REMOVE:    'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  OPTION_ADD:        'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400',
  OPTION_UPDATE:     'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-400',
  OPTION_REMOVE:     'bg-red-100 text-red-700 dark:bg-red-500/10 dark:text-red-400',
  TARGET_URL_ADD:    'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  TARGET_URL_REMOVE: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

// ─── Dot ─────────────────────────────────────────────────────────────────────

export interface DotStyle { bg: string; icon: string }

const DOT_STYLES: Record<FormTimelineVisualType, DotStyle> = {
  CREATE:            { bg: 'bg-green-100 dark:bg-green-500/20',    icon: 'text-green-600 dark:text-green-400' },
  UPDATE:            { bg: 'bg-blue-100 dark:bg-blue-500/20',      icon: 'text-blue-600 dark:text-blue-400' },
  DELETE:            { bg: 'bg-red-100 dark:bg-red-500/20',        icon: 'text-red-600 dark:text-red-400' },
  PUBLISH:           { bg: 'bg-teal-100 dark:bg-teal-500/20',      icon: 'text-teal-600 dark:text-teal-400' },
  PAUSE:             { bg: 'bg-amber-100 dark:bg-amber-500/20',    icon: 'text-amber-600 dark:text-amber-400' },
  ARCHIVE:           { bg: 'bg-gray-200 dark:bg-gray-700',         icon: 'text-gray-500 dark:text-gray-400' },
  UNARCHIVE:         { bg: 'bg-emerald-100 dark:bg-emerald-500/20',icon: 'text-emerald-600 dark:text-emerald-400' },
  FIELD_ADD:         { bg: 'bg-blue-100 dark:bg-blue-500/20',      icon: 'text-blue-600 dark:text-blue-400' },
  FIELD_UPDATE:      { bg: 'bg-blue-100 dark:bg-blue-500/20',      icon: 'text-blue-600 dark:text-blue-400' },
  FIELD_REMOVE:      { bg: 'bg-red-100 dark:bg-red-500/20',        icon: 'text-red-600 dark:text-red-400' },
  SETTING_INIT:      { bg: 'bg-purple-100 dark:bg-purple-500/20',  icon: 'text-purple-600 dark:text-purple-400' },
  SETTING_UPDATE:    { bg: 'bg-purple-100 dark:bg-purple-500/20',  icon: 'text-purple-600 dark:text-purple-400' },
  CRM_MAP_ADD:       { bg: 'bg-cyan-100 dark:bg-cyan-500/20',      icon: 'text-cyan-600 dark:text-cyan-400' },
  CRM_MAP_UPDATE:    { bg: 'bg-cyan-100 dark:bg-cyan-500/20',      icon: 'text-cyan-600 dark:text-cyan-400' },
  CRM_MAP_REMOVE:    { bg: 'bg-red-100 dark:bg-red-500/20',        icon: 'text-red-600 dark:text-red-400' },
  OPTION_ADD:        { bg: 'bg-indigo-100 dark:bg-indigo-500/20',  icon: 'text-indigo-600 dark:text-indigo-400' },
  OPTION_UPDATE:     { bg: 'bg-indigo-100 dark:bg-indigo-500/20',  icon: 'text-indigo-600 dark:text-indigo-400' },
  OPTION_REMOVE:     { bg: 'bg-red-100 dark:bg-red-500/20',        icon: 'text-red-600 dark:text-red-400' },
  TARGET_URL_ADD:    { bg: 'bg-gray-100 dark:bg-gray-700',         icon: 'text-gray-500 dark:text-gray-400' },
  TARGET_URL_REMOVE: { bg: 'bg-gray-100 dark:bg-gray-700',         icon: 'text-gray-500 dark:text-gray-400' },
};

// ─── Icons (heroicons stroke, viewBox 0 0 24 24) ─────────────────────────────

const ICON_PATHS: Record<FormTimelineVisualType, string> = {
  CREATE:
    'M12 9v3m0 0v3m0-3h3m-3 0H9m12 0a9 9 0 11-18 0 9 9 0 0118 0z',
  UPDATE:
    'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  DELETE:
    'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  PUBLISH:
    'M12 19l9 2-9-18-9 18 9-2zm0 0v-8',
  PAUSE:
    'M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z',
  ARCHIVE:
    'M5 8h14M5 8a2 2 0 110-4h14a2 2 0 110 4M5 8v10a2 2 0 002 2h10a2 2 0 002-2V8m-9 4h4',
  UNARCHIVE:
    'M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12',
  FIELD_ADD:
    'M9 13h6m-3-3v6m5 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z',
  FIELD_UPDATE:
    'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  FIELD_REMOVE:
    'M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16',
  SETTING_INIT:
    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
  SETTING_UPDATE:
    'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065zM15 12a3 3 0 11-6 0 3 3 0 016 0z',
  CRM_MAP_ADD:
    'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  CRM_MAP_UPDATE:
    'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  CRM_MAP_REMOVE:
    'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  OPTION_ADD:
    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  OPTION_UPDATE:
    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  OPTION_REMOVE:
    'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01',
  TARGET_URL_ADD:
    'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
  TARGET_URL_REMOVE:
    'M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
};

const FALLBACK_DOT: DotStyle = { bg: 'bg-gray-100 dark:bg-gray-700', icon: 'text-gray-500 dark:text-gray-400' };

export function formEntryBadgeLabel(vt: FormTimelineVisualType): string  { return BADGE_LABELS[vt]  ?? 'EVENTO'; }
export function formEntryBadgeClass(vt: FormTimelineVisualType): string  { return BADGE_CLASSES[vt] ?? 'bg-gray-100 text-gray-600'; }
export function formEntryDotStyle(vt: FormTimelineVisualType): DotStyle  { return DOT_STYLES[vt]    ?? FALLBACK_DOT; }
export function formEntryIconPath(vt: FormTimelineVisualType): string    { return ICON_PATHS[vt]    ?? 'M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'; }
